import type React from "react";
import { useEffect, useImperativeHandle, useRef } from "react";
import { twJoin } from "tailwind-merge";

import { autoCompleteDisabledProperties } from "@/helpers/auto-complete-helpers";

export interface CodeInputHandle {
  focus: () => void;
  clear: () => void;
}

export interface CodeInputProps {
  ref?: React.Ref<CodeInputHandle>;
  "aria-label": string;
  autoFocus?: boolean;
  disabled?: boolean;
  length?: number;
  placeholder?: string;
  onChange: (value: string) => void;
}

const VALID_CHARACTER_PATTERN = /^[a-zA-Z0-9]$/;

/** A segmented input for fixed-length alphanumeric verification codes. */
export function CodeInput({
  ref,
  "aria-label": ariaLabel,
  autoFocus = true,
  disabled,
  length = 6,
  placeholder,
  onChange,
}: CodeInputProps): React.ReactNode {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const sendResult = () => {
    onChange(inputsRef.current.map((input) => input?.value ?? "").join(""));
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputsRef.current[0]?.focus();
    },
    clear: () => {
      for (const input of inputsRef.current) {
        if (input) {
          input.value = "";
        }
      }
      inputsRef.current[0]?.focus();
      sendResult();
    },
  }));

  useEffect(() => {
    if (autoFocus) {
      inputsRef.current[0]?.focus();
    }
  }, [autoFocus]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, nextElementSibling } = event.currentTarget;
    if (value.length > 1) {
      event.currentTarget.value = value.charAt(0);
      (nextElementSibling as HTMLInputElement | null)?.focus();
    } else if (VALID_CHARACTER_PATTERN.test(value)) {
      (nextElementSibling as HTMLInputElement | null)?.focus();
    } else {
      event.currentTarget.value = "";
    }
    sendResult();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Backspace") {
      return;
    }

    const target = event.currentTarget;
    if (target.value === "") {
      const previousInput = target.previousElementSibling as HTMLInputElement | null;
      if (previousInput) {
        previousInput.value = "";
        previousInput.focus();
        event.preventDefault();
      }
    } else {
      target.value = "";
    }
    sendResult();
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedValue = event.clipboardData.getData("Text");
    let currentInput = 0;

    for (const pastedCharacter of pastedValue) {
      const input = inputsRef.current[currentInput];
      if (input && VALID_CHARACTER_PATTERN.test(pastedCharacter) && !input.value) {
        input.value = pastedCharacter;
        const nextInput = input.nextElementSibling as HTMLInputElement | null;
        if (nextInput) {
          nextInput.focus();
          currentInput++;
        }
      }
    }
    sendResult();
    event.preventDefault();
  };

  return (
    <div role="group" aria-label={ariaLabel} className="flex justify-center gap-space-sm" data-testid="code-input">
      {Array.from({ length }, (_, index) => (
        <input
          className={twJoin(
            "h-control-md w-code-input-cell appearance-none rounded-lg border border-border-secondary bg-background-default p-0 text-center text-heading-2 ring-4 ring-transparent transition-colors duration-200 hover:border-border-brand-secondary focus-visible:border-border-brand-secondary focus-visible:ring-border-default focus-visible:outline-hidden",
            disabled &&
              "border-border-secondary bg-background-secondary text-text-secondary hover:border-border-secondary",
          )}
          key={index}
          type="text"
          ref={(element) => {
            inputsRef.current[index] = element;
          }}
          maxLength={1}
          {...(index === 0 ? { autoComplete: "one-time-code" } : autoCompleteDisabledProperties)}
          aria-label={`${ariaLabel} ${index + 1}`}
          disabled={disabled}
          placeholder={placeholder}
          pattern={VALID_CHARACTER_PATTERN.source}
          enterKeyHint="done"
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={(event) => event.currentTarget.select()}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
}
